import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Sparkles, Loader2, Copy, Megaphone, Mail, PenLine, Lightbulb, Megaphone as Ad } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TYPES = [
  { id: "social_post", label: "Social Post", icon: Megaphone },
  { id: "email", label: "Email", icon: Mail },
  { id: "blog_ideas", label: "Blog Ideas", icon: Lightbulb },
  { id: "ad_copy", label: "Ad Copy", icon: Ad },
];
const TONES = ["professional", "friendly", "upbeat", "bold", "witty", "empathetic"];

export default function Marketing() {
  const qc = useQueryClient();
  const { refreshUser } = useAuth();
  const [type, setType] = useState("social_post");
  const [tone, setTone] = useState("professional");
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [output, setOutput] = useState("");

  const { data: history = [] } = useQuery({ queryKey: ["content"], queryFn: async () => (await api.get("/ai/content")).data });

  const generate = async () => {
    if (!prompt.trim()) return;
    setLoading(true); setOutput("");
    try {
      const res = await api.post("/ai/content", { content_type: type, prompt, tone });
      setOutput(res.data.output);
      refreshUser();
      qc.invalidateQueries({ queryKey: ["content"] });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); };

  return (
    <div>
      <PageHeader title="Content Studio" subtitle="Generate on-brand marketing content in seconds." testId="marketing-header" />
      <div className="grid gap-6 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <div className="rounded-2xl border border-border bg-card p-5 sm:p-6">
            <Label className="mb-2 block">Content type</Label>
            <div className="grid grid-cols-2 gap-2">
              {TYPES.map((t) => (
                <button key={t.id} onClick={() => setType(t.id)} data-testid={`content-type-${t.id}`}
                  className={`flex items-center gap-2 rounded-xl border p-3 text-sm font-medium transition-all duration-200 ${type === t.id ? "border-accent bg-accent/10 text-accent" : "border-border hover:bg-secondary"}`}>
                  <t.icon className="h-4 w-4" /> {t.label}
                </button>
              ))}
            </div>
            <div className="mt-4 space-y-1.5">
              <Label>Tone</Label>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger data-testid="content-tone-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TONES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="mt-4 space-y-1.5">
              <Label>What do you want to say?</Label>
              <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4} placeholder="e.g. Announce a 20% discount on our consulting retainer for new clients this month." data-testid="content-prompt-input" />
            </div>
            <Button className="mt-4 w-full" onClick={generate} disabled={loading || !prompt.trim()} data-testid="generate-content-button">
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />} Generate
            </Button>
          </div>
        </div>

        <div className="lg:col-span-3">
          {output ? (
            <div className="rounded-2xl border border-border bg-card p-5 sm:p-6 animate-fade-up" data-testid="content-output">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm font-semibold text-accent flex items-center gap-1.5"><Sparkles className="h-4 w-4" /> Generated</span>
                <Button size="sm" variant="ghost" onClick={() => copy(output)} data-testid="copy-content-button"><Copy className="mr-1.5 h-3.5 w-3.5" /> Copy</Button>
              </div>
              <div className="whitespace-pre-wrap text-sm leading-relaxed">{output}</div>
            </div>
          ) : (
            <EmptyState icon={PenLine} title="Your content appears here" description="Pick a type, add a prompt, and let AI draft it for you." testId="marketing-empty" />
          )}

          {history.length > 0 && (
            <div className="mt-6">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Recent generations</p>
              <div className="space-y-2">
                {history.slice(0, 5).map((h) => (
                  <button key={h.id} onClick={() => { setOutput(h.output); setType(h.content_type); }} className="block w-full rounded-xl border border-border bg-card p-3 text-left transition-colors hover:bg-secondary" data-testid={`content-history-${h.id}`}>
                    <p className="text-xs font-semibold capitalize text-accent">{h.content_type.replace("_", " ")}</p>
                    <p className="mt-0.5 line-clamp-1 text-sm text-muted-foreground">{h.prompt}</p>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
